import { Component, inject, OnInit, signal, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import { ConfirmationModalComponent, DataTableComponent } from "../../../shared/components";
import { genericFloatButtons } from "../../../shared/components/general/float-action";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, PermissionAction } from "../../../shared/constants/enums";
import { CustomButton, TableButtonClicked } from "../../../shared/models";
import { PermissionsService, SystemLayoutService } from "../../../shared/services";
import { UserRolesApiService } from "../../../shared/services/user-roles-api-service";

@Component({
  selector: "adaa-user-roles-list",
  imports: [DataTableComponent],
  templateUrl: "./user-roles-list.html",
  styleUrl: "./user-roles-list.scss",
})
export class UserRolesListComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _userRolesApiService = inject(UserRolesApiService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _translateService = inject(TranslateService);
  private readonly _modalService = inject(NgbModal);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _systemLayoutService = inject(SystemLayoutService);

  readonly adaaDataTable = viewChild<DataTableComponent>("adaaDataTable");

  private readonly _untilDestroyed = AdaaHelper.untilDestroyed();
  private readonly _genericFloatButtons = genericFloatButtons();
  readonly Constants = Constants;
  readonly customButtons = signal<CustomButton[]>([]);

  public ngOnInit(): void {
    this._floatActions();

    this._permissionsService.isPermissionsLoaded.pipe(this._untilDestroyed()).subscribe((loaded: boolean) => {
      if (!loaded) return;

      this._checkPermissions();
    });

    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe(() => {
      this._router.navigateByUrl("console");
    });
  }

  private _checkPermissions() {
    const permission = this._permissionsService.hasPermission([
      {
        permissionAction: PermissionAction.manage,
        objectTypeId: Constants.CONSTANT_PERMISSIONS.USER_ROLES,
      },
    ]);

    this.customButtons.set([
      {
        iconName: "adaa-icon-adaa-ok-Y",
        eventName: "enable",
        iconTitle: "common.form.label.active",
        onlyIf: (data: { enabled: AdaaBoolean }) => {
          return AdaaBoolean.Y === data?.enabled && permission;
        },
      },
      {
        iconName: "adaa-icon-adaa-ok-N",
        eventName: "disable",
        iconTitle: "common.form.label.inactive",
        onlyIf: (data: { enabled: AdaaBoolean }) => {
          return AdaaBoolean.N === data?.enabled && permission;
        },
      },
    ]);
  }

  public addNew() {
    this._router.navigateByUrl("/console/users-roles/create");
  }

  public getAction(value: TableButtonClicked) {
    switch (value.event) {
      case "enable":
      case "disable":
        this._updateState(value);
        break;
      case "edit":
        this._router.navigateByUrl(`/console/users-roles/edit/${value.data.id}`);
        break;
      case "view":
        this._router.navigateByUrl(`/console/users-roles/${value.data.id}`);
        break;
      case "delete":
        this._deleteRole(value.data.id);
        break;
      default:
        break;
    }
  }

  private _updateState(value: TableButtonClicked) {
    const data = { id: value.data.id, enabled: value.event === "enable" ? "N" : "Y" };
    this._userRolesApiService.updateState(data).subscribe({
      next: () => {
        this._toastrService.success(
          this._translateService.instant(
            "notification.success." + (value.event === "enable" ? "deactivate" : "activate")
          )
        );
        this.adaaDataTable()?.loadTableData();
      },
    });
  }

  private _deleteRole(roleId: number) {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
    });

    modal.componentInstance.header = "user_roles.modals.delete_yes_no.title";
    modal.componentInstance.title = "user_roles.modals.delete_yes_no.info";

    modal.result.then((e) => {
      if (e) {
        this._userRolesApiService.deleteRole(roleId).subscribe({
          next: () => {
            this._toastrService.success(this._translateService.instant("notification.success.remove"));
            this.adaaDataTable()?.loadTableData();
          },
        });
      }
    });
  }

  private _floatActions(): void {
    this._genericFloatButtons([
      {
        key: "print",
        data: undefined,
      },
    ]);
  }
}
